import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEye, FaPaperclip, FaSave, FaTrash, FaUserPlus } from 'react-icons/fa';
import {
  createReportIncidentAssignment,
  deleteReportIncidentAssignment,
  getReportIncidentDepartments,
  getReportIncidentAssignments,
  getReportIncidents,
  updateReportIncidentStatus
} from '../../services/reportIncidentService';
import { BASE_URL } from '../../services/api';
import './ReportIncidents.scss';

const statusOptions = [
  { value: '', label: 'Tümü' },
  { value: 'New', label: 'Yeni' },
  { value: 'InProgress', label: 'İşlemde' },
  { value: 'Resolved', label: 'Çözüldü' },
  { value: 'Rejected', label: 'Reddedildi' }
];

const statusLabels = {
  New: 'Yeni',
  InProgress: 'İşlemde',
  Resolved: 'Çözüldü',
  Rejected: 'Reddedildi'
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR');
};

const resolveFileUrl = (path) => {
  if (!path) return '#';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path}`;
};

const ReportIncidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [filters, setFilters] = useState({ status: '', departmentGuid: '' });
  const [loading, setLoading] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: 'InProgress', staffNote: '' });
  const [assignmentForm, setAssignmentForm] = useState({ userEmail: '', departmentGuid: '' });

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const role = (user.userType || user.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === '99';

  useEffect(() => {
    loadDepartments();
    loadIncidents();
    if (isAdmin) loadAssignments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await getReportIncidentDepartments();
      setDepartments(response?.isSuccess && Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Departments could not be loaded', error);
    }
  };

  const loadIncidents = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const response = await getReportIncidents(nextFilters);
      if (response?.isSuccess) {
        setIncidents(response.data || []);
      } else {
        toast.error(response?.resultMessage || 'Bildirimler yüklenemedi.');
      }
    } catch (error) {
      toast.error('Bildirimler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await getReportIncidentAssignments();
      if (response?.isSuccess) {
        setAssignments(response.data || []);
      }
    } catch (error) {
      console.error('Assignments could not be loaded', error);
    }
  };

  const openDetails = (incident) => {
    setSelectedIncident(incident);
    setStatusForm({
      status: incident.status || 'InProgress',
      staffNote: incident.staffNote || ''
    });
  };

  const handleFilterChange = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
    loadIncidents(next);
  };

  const saveStatus = async () => {
    if (!selectedIncident) return;

    try {
      const response = await updateReportIncidentStatus(selectedIncident.id, statusForm);
      if (response?.isSuccess) {
        toast.success('Bildirim durumu güncellendi.');
        setSelectedIncident(null);
        loadIncidents();
      } else {
        toast.error(response?.resultMessage || 'Durum güncellenemedi.');
      }
    } catch (error) {
      toast.error('Durum güncellenemedi.');
    }
  };

  const saveAssignment = async () => {
    const department = departments.find((item) => item.guid === assignmentForm.departmentGuid);
    if (!assignmentForm.userEmail || !department) {
      toast.error('E-posta ve birim seçiniz.');
      return;
    }

    try {
      const response = await createReportIncidentAssignment({
        userEmail: assignmentForm.userEmail,
        departmentGuid: assignmentForm.departmentGuid,
        departmentName: department.name
      });
      if (response?.isSuccess) {
        toast.success('Yetki ataması kaydedildi.');
        setAssignmentForm({ userEmail: '', departmentGuid: '' });
        loadAssignments();
      }
    } catch (error) {
      toast.error('Yetki ataması kaydedilemedi.');
    }
  };

  const removeAssignment = async (id) => {
    try {
      const response = await deleteReportIncidentAssignment(id);
      if (response?.isSuccess) {
        toast.success('Yetki ataması pasife alındı.');
        loadAssignments();
      }
    } catch (error) {
      toast.error('Yetki ataması silinemedi.');
    }
  };

  const isImageAttachment = (attachment) =>
    attachment?.mediaType === 'Photo' || attachment?.contentType?.startsWith('image/');

  const isVideoAttachment = (attachment) =>
    attachment?.mediaType === 'Video' || attachment?.contentType?.startsWith('video/');

  return (
    <div className="report-incidents-page">
      <div className="page-header">
        <div>
          <h1>Bize Bildir Yönetimi</h1>
          <p>Vatandaş bildirimlerini ilgili birime göre takip edin.</p>
        </div>
      </div>

      {isAdmin && (
        <section className="assignment-panel">
          <h2>Birim Personeli Yetkilendirme</h2>
          <div className="assignment-form">
            <input
              type="email"
              placeholder="personel@iskenderun.bel.tr"
              value={assignmentForm.userEmail}
              onChange={(e) => setAssignmentForm((prev) => ({ ...prev, userEmail: e.target.value }))}
            />
            <select
              value={assignmentForm.departmentGuid}
              onChange={(e) => setAssignmentForm((prev) => ({ ...prev, departmentGuid: e.target.value }))}
            >
              <option value="">Birim seçiniz</option>
              {departments.map((department) => (
                <option key={department.guid} value={department.guid}>
                  {department.name}
                </option>
              ))}
            </select>
            <button type="button" onClick={saveAssignment}>
              <FaUserPlus /> Yetki Ver
            </button>
          </div>
          <div className="assignment-list">
            {assignments.map((assignment) => (
              <div key={assignment.id} className={assignment.isActive ? '' : 'is-passive'}>
                <span>{assignment.userEmail}</span>
                <strong>{assignment.departmentName}</strong>
                <button type="button" onClick={() => removeAssignment(assignment.id)} disabled={!assignment.isActive}>
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="incidents-table">
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Vatandaş</th>
              <th>Telefon</th>
              <th>Birim</th>
              <th>Konum</th>
              <th>Durum</th>
              <th>Ek</th>
              <th />
            </tr>
            <tr className={`table-filter-row ${isAdmin ? 'has-department-filter' : 'status-only'}`}>
              <th />
              <th />
              <th />
              <th>
                {isAdmin && (
                  <select value={filters.departmentGuid} onChange={(e) => handleFilterChange('departmentGuid', e.target.value)}>
                    <option value="">Tüm birimler</option>
                    {departments.map((department) => (
                      <option key={department.guid} value={department.guid}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                )}
              </th>
              <th />
              <th>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}>Yükleniyor...</td></tr>
            ) : incidents.length === 0 ? (
              <tr><td colSpan={8}>Bildirim bulunamadı.</td></tr>
            ) : incidents.map((incident) => (
              <tr key={incident.id}>
                <td>{formatDate(incident.createdDate)}</td>
                <td>{incident.firstName} {incident.lastName}</td>
                <td>{incident.phone}</td>
                <td>{incident.departmentName}</td>
                <td>{incident.eventLocation}</td>
                <td><span className={`status-pill ${incident.status}`}>{statusLabels[incident.status] || incident.status}</span></td>
                <td>{incident.attachments?.length > 0 ? <><FaPaperclip /> {incident.attachments.length}</> : '-'}</td>
                <td>
                  <button type="button" className="icon-button" onClick={() => openDetails(incident)}>
                    <FaEye />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={!!selectedIncident} onHide={() => setSelectedIncident(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bildirim Detayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedIncident && (
            <div className="incident-detail">
              <div className="detail-grid">
                <div><span>Vatandaş</span><strong>{selectedIncident.firstName} {selectedIncident.lastName}</strong></div>
                <div><span>Telefon</span><strong>{selectedIncident.phone}</strong></div>
                <div><span>Birim</span><strong>{selectedIncident.departmentName}</strong></div>
                <div><span>Tarih</span><strong>{formatDate(selectedIncident.createdDate)}</strong></div>
              </div>
              <div className="detail-block">
                <span>Olay Konumu</span>
                <p>{selectedIncident.eventLocation}</p>
              </div>
              <div className="detail-block">
                <span>Olay</span>
                <p>{selectedIncident.eventDescription}</p>
              </div>
              {selectedIncident.attachments?.length > 0 && (
                <div className="attachments">
                  <span>Ekler</span>
                  <div className="attachment-preview-grid">
                    {selectedIncident.attachments.map((attachment) => (
                      <button
                        key={attachment.id}
                        type="button"
                        className="attachment-preview-card"
                        onClick={() => setPreviewAttachment(attachment)}
                      >
                        {isImageAttachment(attachment) ? (
                          <img src={resolveFileUrl(attachment.filePath)} alt={attachment.fileName} />
                        ) : isVideoAttachment(attachment) ? (
                          <video src={resolveFileUrl(attachment.filePath)} muted />
                        ) : (
                          <div className="attachment-file-placeholder">
                            <FaPaperclip />
                          </div>
                        )}
                        <strong>{attachment.mediaType === 'Video' ? 'Video' : 'Fotoğraf'}</strong>
                        <small>{attachment.fileName}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="incident-history">
                <span>Düzenleme Geçmişi</span>
                <div className="history-list">
                  {(selectedIncident.histories?.length > 0
                    ? selectedIncident.histories
                    : [{
                        id: 'created',
                        actionText: 'Başvuru yapıldı',
                        createdBy: 'Vatandaş',
                        createdDate: selectedIncident.createdDate,
                        newStatus: selectedIncident.status
                      }]
                  ).map((history) => (
                    <div key={history.id} className="history-item">
                      <div className="history-dot" />
                      <div>
                        <strong>{history.actionText || 'Durum güncellendi'}</strong>
                        <p>
                          {formatDate(history.createdDate)}
                          {history.createdBy ? ` - ${history.createdBy}` : ''}
                        </p>
                        {history.oldStatus && history.newStatus && (
                          <small>
                            {statusLabels[history.oldStatus] || history.oldStatus}
                            {' -> '}
                            {statusLabels[history.newStatus] || history.newStatus}
                          </small>
                        )}
                        {history.note && <em>{history.note}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="status-form">
                <label>Durum</label>
                <select value={statusForm.status} onChange={(e) => setStatusForm((prev) => ({ ...prev, status: e.target.value }))}>
                  {statusOptions.filter((item) => item.value).map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
                <label>Personel Notu</label>
                <textarea
                  rows={4}
                  value={statusForm.staffNote}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, staffNote: e.target.value }))}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="secondary-button" onClick={() => setSelectedIncident(null)}>Kapat</button>
          <button type="button" className="primary-button" onClick={saveStatus}><FaSave /> Kaydet</button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!previewAttachment} onHide={() => setPreviewAttachment(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewAttachment?.fileName || 'Ek Önizleme'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewAttachment && (
            <div className="attachment-lightbox">
              {isImageAttachment(previewAttachment) ? (
                <img src={resolveFileUrl(previewAttachment.filePath)} alt={previewAttachment.fileName} />
              ) : isVideoAttachment(previewAttachment) ? (
                <video src={resolveFileUrl(previewAttachment.filePath)} controls />
              ) : (
                <a href={resolveFileUrl(previewAttachment.filePath)} target="_blank" rel="noreferrer">
                  Dosyayı yeni sekmede aç
                </a>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ReportIncidents;
